import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Globe, Loader2, CheckCircle2, XCircle, Save, Plug, Power } from "lucide-react";

export default function ProxySettings({ open, onClose }) {
  const [state, setState] = useState({ enabled: false, has_url: false, masked: null, source: null });
  const [url, setUrl] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testRes, setTestRes] = useState(null);

  const load = () => api.get("/settings/proxy").then((r) => { setState(r.data); setEnabled(r.data.enabled); }).catch(() => {});
  useEffect(() => { if (open) { load(); setUrl(""); setTestRes(null); } }, [open]);

  const payload = () => (url.trim() ? { url: url.trim(), enabled } : { enabled });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/proxy", payload());
      setState(data); setUrl("");
      toast.success(data.enabled ? "Proxy kaydedildi ve AKTİF" : "Proxy kaydedildi (pasif)");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setTesting(true); setTestRes(null);
    try {
      const { data } = await api.post("/settings/proxy/test", url.trim() ? { url: url.trim() } : {});
      setTestRes(data);
      if (data.ok) toast.success(`Proxy çalışıyor · IP: ${data.ip}`);
      else toast.error(`Proxy hatası: ${data.error || data.status}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const testDirect = async () => {
    setTesting(true); setTestRes(null);
    try {
      const { data } = await api.post("/settings/proxy/test-direct");
      setTestRes(data);
      if (data.ok) toast.success(`Doğrudan bağlantı çalışıyor · IP: ${data.ip}`);
      else toast.error(`Doğrudan bağlantı hatası: ${data.error || data.status}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const clearProxy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/settings/proxy/clear");
      setState(data); setUrl(""); setEnabled(false); setTestRes(null);
      toast.success("Proxy ayarı tamamen temizlendi — doğrudan bağlantı kullanılacak");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg overflow-x-hidden" data-testid="proxy-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><Globe className="h-5 w-5 text-brand-green" /> Canlı Fiyat Proxy Ayarı</DialogTitle>
          <DialogDescription>Rakip fiyat sitelerini engellenmeden taramak için residential proxy bağlantısı girin. Aboneliğinizi aldığınızda bağlantıyı buraya yapıştırıp aktif edin.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md bg-slate-50 p-3 text-sm ring-1 ring-inset ring-slate-200">
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Mevcut durum</span>
              <span className={`inline-flex items-center gap-1 text-xs font-semibold ${state.enabled ? "text-emerald-600" : "text-slate-400"}`}>
                <Power className="h-3.5 w-3.5" /> {state.enabled ? "AKTİF" : "Pasif"}
              </span>
            </div>
            <div className="mt-1 flex items-start justify-between gap-2">
              <span className="shrink-0 text-slate-500">Adres</span>
              <span className="tabular break-all text-right text-xs text-slate-700">{state.has_url ? state.masked : "tanımlı değil (doğrudan bağlantı)"}</span>
            </div>
          </div>

          <div className="min-w-0">
            <label className="overline mb-1 block">Proxy Bağlantısı</label>
            <Input data-testid="proxy-url" value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="http://kullanıcı:şifre@host:port" className="w-full min-w-0 font-mono text-sm" />
            <p className="mt-1 text-[11px] text-slate-400">Boş bırakırsanız mevcut adres korunur. Şifreniz güvenli saklanır, ekranda maskelenir.</p>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700" data-testid="proxy-enabled-label">
            <input type="checkbox" data-testid="proxy-enabled" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4 accent-emerald-600" />
            Proxy'yi aktif et (kapalıyken doğrudan bağlantı denenir)
          </label>

          {testRes && (
            <div className={`flex items-center gap-2 rounded-md p-2.5 text-sm ${testRes.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
              {testRes.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
              {testRes.ok ? `Bağlantı başarılı · Çıkış IP: ${testRes.ip}` : `Hata: ${testRes.error || testRes.status}`}
            </div>
          )}

          <div className="flex gap-2">
            <Button data-testid="proxy-test" onClick={test} disabled={testing} variant="outline" className="flex-1">
              {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plug className="mr-2 h-4 w-4" />} Proxy Test Et
            </Button>
            <Button data-testid="proxy-save" onClick={save} disabled={busy} className="flex-1 bg-brand-green text-white hover:bg-brand-green-600">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Kaydet
            </Button>
          </div>
          <div className="flex gap-2">
            <Button data-testid="proxy-test-direct" onClick={testDirect} disabled={testing} variant="outline" className="flex-1 border-sky-200 text-sky-700 hover:bg-sky-50">
              <Plug className="mr-2 h-4 w-4" /> Doğrudan Bağlantıyı Test Et
            </Button>
            <Button data-testid="proxy-clear" onClick={clearProxy} disabled={busy} variant="outline" className="flex-1 border-red-200 text-red-600 hover:bg-red-50">
              <Power className="mr-2 h-4 w-4" /> Proxy Ayarını Temizle
            </Button>
          </div>
          <p className="text-[11px] text-slate-400">Proxy kapalıyken sistem <b>doğrudan (proxysiz)</b> bağlanır; ortam değişkeni proxy'leri (HTTP_PROXY vb.) yok sayılır. Geçersiz/loopback adresler (127.0.0.1:65535 gibi) hiçbir istekte kullanılmaz.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
